import { SellerAnalysisSettingsView } from './SellerAnalysisSettingsView';
import { SellerAdsAgentSettingsView } from './SellerAdsAgentSettingsView';
import { SellerListingsSettingsView } from './SellerListingsSettingsView';
import { SellerPromotionsSettingsView } from './SellerPromotionsSettingsView';
import { SellerBoostSettingsView } from './SellerBoostSettingsView';
import { SellerAdsSyncSettingsView } from './SellerAdsSyncSettingsView';
import { Drawer } from './sellerUi';

// Painel "Automacoes". Cada tela tem "Rotinas" (o que roda sozinho) e "Regras"
// (o que a IA considera bom/ruim). Tudo salva sozinho.
export type SettingsTab = 'anuncios-rotinas' | 'anuncios-regras' | 'impulsionar' | 'promocoes' | 'ads-rotinas' | 'ads-regras';
export type SettingsSectionId = 'hoje' | 'anuncios' | 'promocoes' | 'ads' | 'agente';

const TAB_LABELS: Record<SettingsTab, string> = {
  'anuncios-rotinas': 'Rotinas de anúncios',
  'anuncios-regras': 'Regras da IA',
  impulsionar: 'Fila de impulsionar',
  promocoes: 'Promoções',
  'ads-rotinas': 'Rotinas de Ads',
  'ads-regras': 'Metas e regras de Ads',
};

// `settingsTab` que o backend manda no status das automacoes (sellerAutomationService)
// e links antigos /seller/settings?tab=...
const LEGACY_TABS: Record<string, SettingsTab> = {
  listings: 'anuncios-rotinas',
  health: 'anuncios-rotinas',
  boost: 'impulsionar',
  promotions: 'promocoes',
  'ads-sync': 'ads-rotinas',
  'ads-agent': 'ads-rotinas',
};

export function normalizeSettingsTab(value: string | null | undefined): SettingsTab | null {
  if (!value) return null;
  if (value in TAB_LABELS) return value as SettingsTab;
  return LEGACY_TABS[value] || null;
}

export const SETTINGS_TAB_SECTION: Record<SettingsTab, SettingsSectionId> = {
  'anuncios-rotinas': 'anuncios',
  'anuncios-regras': 'anuncios',
  impulsionar: 'anuncios',
  promocoes: 'promocoes',
  'ads-rotinas': 'ads',
  'ads-regras': 'ads',
};

// Cada tela so mostra as abas que afetam o que esta na frente do usuario.
export const SETTINGS_TABS_BY_SECTION: Record<SettingsSectionId, SettingsTab[]> = {
  hoje: ['anuncios-rotinas', 'promocoes', 'ads-rotinas', 'impulsionar', 'anuncios-regras', 'ads-regras'],
  anuncios: ['anuncios-rotinas', 'anuncios-regras', 'impulsionar'],
  promocoes: ['promocoes'],
  ads: ['ads-rotinas', 'ads-regras'],
  agente: ['anuncios-rotinas', 'promocoes', 'ads-rotinas', 'impulsionar', 'anuncios-regras', 'ads-regras'],
};

export function SellerSettingsDrawer({
  tab, tabs, onTab, onClose,
}: {
  tab: SettingsTab;
  tabs: SettingsTab[];
  onTab: (tab: SettingsTab) => void;
  onClose: () => void;
}) {
  // Badge de outra tela pode pedir uma aba fora da lista desta secao.
  const visibleTabs = tabs.includes(tab) ? tabs : [...tabs, tab];
  return (
    <Drawer open onClose={onClose} title="Automações" subtitle="O que roda sozinho e as regras da IA. Vale para a conta inteira e salva automaticamente." width="max-w-3xl">
      {visibleTabs.length > 1 && (
        <div className="mb-5 flex max-w-full gap-1 overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 p-1">
          {visibleTabs.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => onTab(id)}
              className={`shrink-0 rounded-md px-3 py-1.5 text-xs font-medium transition ${tab === id ? 'bg-white text-orange-800 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              {TAB_LABELS[id]}
            </button>
          ))}
        </div>
      )}
      {tab === 'anuncios-rotinas' && <SellerListingsSettingsView />}
      {tab === 'anuncios-regras' && <SellerAnalysisSettingsView />}
      {tab === 'impulsionar' && <SellerBoostSettingsView />}
      {tab === 'promocoes' && <SellerPromotionsSettingsView />}
      {tab === 'ads-rotinas' && <SellerAdsSyncSettingsView />}
      {tab === 'ads-regras' && <SellerAdsAgentSettingsView />}
    </Drawer>
  );
}
